import React from "react";
import TradingModeChartWorkspace from "../chartWorkspace/TradingModeChartWorkspace";
import "./PaperAutoTradeChart.css";

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

/**
 * Paper AutoTrade chart adapter.
 *
 * AutoTrade keeps its simulated execution/risk engine, but the market chart is
 * the canonical professional AninaTrade chart. No local candlestick renderer
 * lives here anymore.
 */
function PaperAutoTradeChart({
  symbol,
  direction,
  estimatedPrice,
  entryPrice,
  stopLoss,
  profitTarget,
}) {
  const cleanSymbol = String(symbol || "AAPL").trim().toUpperCase();
  const normalizedDirection =
    String(direction || "Long").toLowerCase() === "short" ? "Short" : "Long";

  return (
    <section
      className="paperAutoTradeChart paperAutoTradePanel paperAutoTradeChartShared"
      aria-labelledby="autotrade-chart-title"
      aria-label="Paper Auto shared chart"
      data-direction={normalizedDirection.toLowerCase()}
      data-chart-engine="anina-professional-shared"
    >
      <header className="paperAutoTradeChartHeader">
        <div>
          <span>PAPER AUTO · SHARED PROFESSIONAL CHART</span>
          <h2 id="autotrade-chart-title">{cleanSymbol} Technical Chart</h2>
          <p>
            Authenticated market chart · Authenticated market chart · automated execution is paper-only
          </p>
        </div>
        <div className="paperAutoTradeChartQuote">
          <span>Paper Auto reference</span>
          <strong>{money(estimatedPrice)}</strong>
        </div>
      </header>

      <div className="paperAutoTradeSetupSummary" aria-label="Paper Auto trade plan">
        <span>
          Direction <strong>{normalizedDirection}</strong>
        </span>
        <span
          aria-label={`Paper Auto entry ${Number(entryPrice).toFixed(2)}`}
          data-level="entry"
          data-price={Number(entryPrice).toFixed(2)}
        >
          Entry <strong>{money(entryPrice)}</strong>
        </span>
        <span
          aria-label={`Paper Auto stop ${Number(stopLoss).toFixed(2)}`}
          data-level="stop"
          data-price={Number(stopLoss).toFixed(2)}
        >
          Stop <strong>{money(stopLoss)}</strong>
        </span>
        <span
          aria-label={`Paper Auto target ${Number(profitTarget).toFixed(2)}`}
          data-level="target"
          data-price={Number(profitTarget).toFixed(2)}
        >
          Target <strong>{money(profitTarget)}</strong>
        </span>
      </div>

      <TradingModeChartWorkspace
        symbol={cleanSymbol}
        modeLabel="PAPER AUTO"
        executionLabel="AUTOMATED PAPER EXECUTION ONLY"
        entry={entryPrice}
        stop={stopLoss}
        targets={{ target1: profitTarget }}
        direction={normalizedDirection}
      />

      <footer className="paperAutoTradeChartFooter">
        <span>Same professional chart engine as Live Trading</span>
        <span>No live-money order routing</span>
      </footer>
    </section>
  );
}

// Kept temporarily for backward-compatible deterministic execution fixtures and
// existing tests. These helpers no longer render the Paper Auto market chart.
export function createAutoTradeChartData(symbol, referencePrice, count = 54) {
  const seed = [...symbol].reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  const basePrice = Number(referencePrice) || 100;
  let previousClose = basePrice * 0.988;

  return Array.from({ length: count }, (_, index) => {
    const wave = Math.sin((index + seed) * 0.61) * 0.0048;
    const secondary = Math.cos((index + seed) * 0.23) * 0.0026;
    const drift = 0.00028;
    const open =
      previousClose * (1 + Math.sin(index * 1.17 + seed) * 0.0012);
    const close = open * (1 + wave + secondary + drift);
    const wick =
      open * (0.0018 + Math.abs(Math.sin(index + seed)) * 0.0022);
    const high = Math.max(open, close) + wick;
    const low = Math.min(open, close) - wick * 0.85;
    const volume =
      400000 +
      ((seed * 3571 + index * 7919) % 1600000) *
        (0.75 + Math.abs(Math.cos(index * 0.37 + seed)) * 0.5);
    previousClose = close;
    return {
      id: `${symbol}-${index}`,
      open,
      high,
      low,
      close,
      volume: Math.round(volume),
    };
  });
}

export function calculateAutoTradeStudies(candles) {
  const closes = candles.map((candle) => candle.close);
  const ema9 = ema(closes, 9);
  const ema20 = ema(closes, 20);
  const vwap = [];
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;

  candles.forEach((candle) => {
    const typicalPrice = (candle.high + candle.low + candle.close) / 3;
    cumulativePriceVolume += typicalPrice * candle.volume;
    cumulativeVolume += candle.volume;
    vwap.push(cumulativePriceVolume / cumulativeVolume);
  });

  return { vwap, ema9, ema20 };
}

function ema(values, period) {
  const multiplier = 2 / (period + 1);
  return values.reduce((result, value, index) => {
    result.push(
      index === 0
        ? value
        : value * multiplier + result[index - 1] * (1 - multiplier),
    );
    return result;
  }, []);
}

export default PaperAutoTradeChart;
